import React, { useState, useEffect, useRef } from "react";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import { useDispatch, useSelector } from "react-redux";
import Query from "@arcgis/core/rest/support/Query";
import Graphic from "@arcgis/core/Graphic";
import Select from "react-select";
import { setIsDist, setIsUlb, setIsWard, setIsYearLeft, setIsYearRight } from "../../store/slice/currentStageSlice";

function ChangeTool({ view }) {
   const { isWidth } = useSelector((state) => state.currentStage);
   const currentStageValue = useSelector((state) => state.currentStage.features)

   const [districts, setDistricts] = useState([]); // District list
   const [selectedDistrict, setSelectedDistrict] = useState(""); // Selected district name
   const [districtCode, setDistrictCode] = useState(""); // Selected district code
   const { isTheme } = useSelector((state) => state.currentStage)
   const [ulbs, setUlbs] = useState([]); // ULB list
   const [selectedUlb, setSelectedUlb] = useState(""); // Selected ULB name
   const [ulbCode, setUlbCode] = useState(""); // Selected ULB code
   const authData = useSelector(state => state.authToken.authData);
   const [wards, setWards] = useState([]); // Ward list
   const [selectedWard, setSelectedWard] = useState(""); // Selected ward name
   const [wardCode, setWardCode] = useState(""); // Selected ward code
   const [selectedSearch, setSelectedSearch] = useState("");
   const [searchInput, setSearchInput] = useState("");
   const [propertyInfo, setPropertyInfo] = useState([]);
   const [selectedYearLeft, setSelectedYearLeft] = useState("1");
   const [selectedYearRight, setSelectedYearRight] = useState("8");
   const dispatch = useDispatch()
   const { isDistCd, isUlbCd, isWardNo } = useSelector((state) => state.currentStage)
   const [rightLayerData, setRightLayerData] = useState([]);
   const [leftLayerData, setLeftLayerData] = useState([]);

   useEffect(() => {

      //fetch district from ulb
      const fetchDistrictCodes = async () => {
         const url =
            "https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/94/query?where=1=1&outFields=dist_cd&returnGeometry=false&returnDistinctValues=true&f=geojson";

         try {
            const response = await fetch(url);
            const data = await response.json();

            if (data.features) {
               return data.features.map((feature) => feature.properties.dist_cd);
            }
         } catch (error) {
            console.log("Error fetching district codes:", error);
         }

         return [];
      };

      const loadDistricts = async () => {
         const distCodes = await fetchDistrictCodes();
         if (!distCodes.length) {
            console.log("No district codes available");
            return;
         }

         const query = {
            where: `dist_cd IN (${distCodes.map((code) => `'${code}'`).join(",")})`,
            outFields: "districtname,dist_cd",
            returnGeometry: false,
            returnDistinctValues: true,
            f: "geojson"
         };

         const url = `https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/3/query?${new URLSearchParams(query)}`;

         try {
            const response = await fetch(url);
            const data = await response.json();

            if (authData?.UserType === "districtuser" || authData?.UserType === "User") {
               const formattedDistricts = [{
                  districtName: authData?.DistrictName,
                  districtCode: authData?.DistrictCode,
               }];
               setDistricts(formattedDistricts);
               setDistrictCode(authData?.DistrictCode);
               setSelectedDistrict(authData?.DistrictName);
               zoomToFeatureOne(authData?.DistrictName, "DistrictName", "3"); // 
               await loadUlbs(authData?.DistrictCode)
            } else {
               if (data.features) {
                  const formattedDistricts = data.features.map((f) => ({
                     districtName: f.properties.districtname,
                     districtCode: f.properties.dist_cd,
                  }));
                  setDistricts(formattedDistricts);
               }
            }
         } catch (error) {
            console.log("Error fetching districts:", error);
         }
      };

      loadDistricts();
   }, []);

   useEffect(() => {
      AreaFetch()
   }, [isUlbCd])

   const AreaFetch = () => {
      const yearIdLeft = parseInt(selectedYearLeft);
      const yearIdRight = parseInt(selectedYearRight);

      dispatch(setIsYearLeft(yearIdLeft))
      dispatch(setIsYearRight(yearIdRight))

      const fetchAreaSummary = (layerId, label, isUlbCd) => {
         const queryUrl = `https://urbangis.mp.gov.in/server/rest/services/temp_n/mp_lulc_change/MapServer/${layerId}/query`;

         if (!isUlbCd) {
            console.error('Missing ulb_cd. Aborting fetch.');
            return;
         }

         let whereClause = `ulb_cd='${isUlbCd}'`;

         const params = new URLSearchParams({
            where: whereClause,
            groupByFieldsForStatistics: 'classname',
            outStatistics: JSON.stringify([{
               statisticType: 'sum',
               onStatisticField: 'area_sqkm',
               outStatisticFieldName: 'sum_area'
            }]),
            f: 'json',
            returnGeometry: 'false'
         });

         fetch(`${queryUrl}?${params.toString()}`)
            .then(response => response.json())
            .then(data => {
               console.log("Fetched data for", label, data);
               if (data.features) {
                  if (label === "Right") {
                     setRightLayerData(data.features);
                  } else if (label === "Left") {
                     setLeftLayerData(data.features);
                  }
               } else {
                  if (label === "Right") {
                     setRightLayerData([]);
                  } else if (label === "Left") {
                     setLeftLayerData([]);
                  }
               }
            })
            .catch(error => {
               console.error(`Error fetching summary stats for ${label}:`, error);
            });
      };

      // Example: Call with yearId and required codes
      fetchAreaSummary(yearIdLeft, 'Left', isUlbCd);
      fetchAreaSummary(yearIdRight, 'Right', isUlbCd);
   }

   const handleDistrictChange = async (event) => {
      const selectedValue = event.target.value;
      const selectedDistrictObj = districts.find(d => d.districtCode === selectedValue);

      localStorage.setItem("dis_cd", selectedValue);
      dispatch(setIsDist(selectedValue))

      if (selectedDistrictObj) {
         setSelectedDistrict(selectedDistrictObj.districtName);
         setDistrictCode(selectedDistrictObj.districtCode);

         setUlbs([]);
         setUlbCode("");
         setSelectedUlb("");

         await loadUlbs(selectedDistrictObj.districtCode); // 
         zoomToFeatureOne(selectedDistrictObj.districtName, "DistrictName", "3"); // 
      } else {
         setSelectedDistrict("");
         setDistrictCode("");
         setUlbs([]);
         setUlbCode("");
         setSelectedUlb("");
      }
   };

   const loadUlbs = async (dist_cd) => {
      const url = `https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/5/query?where=dist_cd='${dist_cd}'&returnGeometry=false&outFields=name,ulb_cd&returnDistinctValues=true&f=geojson`;
      try {
         const response = await fetch(url);
         const data = await response.json();
         if (authData?.UserType === "User") {
            const formattedUlbs = [{
               ulbName: authData?.UlbName,
               ulbCode: authData?.Ulb_Code,
            }];
            setUlbs(formattedUlbs);
            setUlbCode(authData?.Ulb_Code);
            setSelectedUlb(authData?.UlbName);
            // await loadWards(authData?.Ulb_Code);
         } else if (data.features) {
            const formattedUlbs = data.features.map(f => ({
               ulbName: f.properties.name,
               ulbCode: f.properties.ulb_cd,
            }));
            setUlbs(formattedUlbs);
         }
      } catch (err) {
         console.error("Error loading ULBs:", err);
      }
   };

   // const loadUlbs = async (dist_cd) => {
   //    const ulbLayerUrl = "https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/94/query";
   //    const ulbQuery = `where=dist_cd='${dist_cd}'&returnGeometry=false&outFields=ub_nm_e,ulb_cd&returnDistinctValues=true&f=geojson`;

   //    try {
   //       const ulbResponse = await fetch(`${ulbLayerUrl}?${ulbQuery}`);
   //       const data = await ulbResponse.json();

   //       if (authData?.UserType === "User") {
   //          const formattedUlbs = [{
   //              ulbName: authData?.UlbName,
   //              ulbCode: authData?.Ulb_Code,
   //          }];
   //          setUlbs(formattedUlbs);
   //          setUlbCode(authData?.Ulb_Code);
   //          setSelectedUlb(authData?.UlbName);
   //          zoomToFeatureTow(authData?.UlbName, "ub_nm_e", "94");
   //       } else if (data.features) {
   //          // FIXED: Use ub_nm_e instead of name
   //          const formattedUlbs = data.features.map(f => ({
   //              ulbName: f.properties.ub_nm_e, // Changed from f.properties.name
   //              ulbCode: f.properties.ulb_cd,
   //          }));
   //          setUlbs(formattedUlbs);
   //       }
   //    } catch (error) {
   //       console.log("Error fetching ULBs:", error);
   //    }
   // };

   const handleUlbChange = async (event) => {
      const selectedValue = event.target.value;
      const selectedUlbObj = ulbs.find(ulb => ulb.ulbCode === selectedValue);

      localStorage.setItem("ulb_cd", selectedValue);
      dispatch(setIsUlb(selectedValue))

      if (selectedUlbObj) {
         setSelectedUlb(selectedUlbObj.ulbName);
         setUlbCode(selectedUlbObj.ulbCode);

         setWards([]);
         setWardCode("");
         setSelectedWard("");

         await AreaFetch();
         // zoomToFeatureTow(selectedUlbObj.ulbName, "ub_nm_e", "94");
         LoadZoom(selectedUlbObj.ulbName)
      } else {
         setSelectedUlb("");
         setUlbCode("");
         setWards([]);
         setWardCode("");
         setSelectedWard("");
      }
   };


   const LoadZoom = (ulbName) => {
      if (ulbName) {
         zoomToUlb(ulbName);
      }
   };
   const mainLayerUrl = "https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/MapServer";


   const zoomToUlb = async (ulbName) => {
      const layer = new FeatureLayer({ url: `${mainLayerUrl.replace('MapServer', 'FeatureServer/5')}` });
      const query = new Query({ where: `name = '${ulbName}'`, returnGeometry: true });

      try {
         const { features } = await layer.queryFeatures(query);
         if (features.length) {
            const graphic = new Graphic({
               geometry: features[0].geometry,
               symbol: {
                  type: "simple-fill",
                  color: [0, 0, 255, 0.0],
                  outline: { color: [0, 0, 255], width: 2 }
               }
            });
            view.graphics.add(graphic);
            view.goTo(features[0].geometry.extent.expand(1.5));
         }
      } catch (error) {
         console.error("Error zooming to ULB:", error);
      }
   };

   const loadWards = async (ulb_cd) => {
      const wardLayerUrl = "https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/95/query";
      const wardQuery = `where=ulb_cd='${ulb_cd}'&returnGeometry=false&outFields=wardname,wardno&returnDistinctValues=true&f=geojson`;

      try {
         const wardResponse = await fetch(`${wardLayerUrl}?${wardQuery}`);
         const wardData = await wardResponse.json();

         if (wardData.features) {
            const formattedWards = wardData.features.map((f) => ({
               wardName: f.properties.wardname,
               wardCode: f.properties.wardno
            }));

            setWards(formattedWards);
            setWardCode("");
            setSelectedWard("");
         }
      } catch (error) {
         console.log("Error fetching wards:", error);
      }
   };

   const handleWardChange = async (event) => {
      const selectedValue = event.target.value;
      const selectedWardObj = wards.find(w => w.wardCode === selectedValue);

      localStorage.setItem("wardno", selectedValue);
      dispatch(setIsWard(selectedValue));

      if (selectedWardObj) {
         setSelectedWard(selectedWardObj.wardName);
         setWardCode(selectedWardObj.wardCode);
         zoomToFeatureThree(selectedWardObj.wardName, "wardname", "95", ulbCode);
         AreaFetch();
      } else {
         setSelectedWard("");
         setWardCode("");
      }
   };

   const zoomToFeatureOne = async (value, fieldName, layerId) => {
      const layer = new FeatureLayer({
         url: `https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/${layerId}`
      });
      const query = new Query();
      query.where = `${fieldName} = '${value}'`;
      query.returnGeometry = true;
      const response = await layer.queryFeatures(query);
      const geometry = response.features[0].geometry;
      const graphic = new Graphic({
         geometry,
         symbol: {
            type: "simple-fill",
            color: null,
            outline: {
               color: [94, 255, 51],
               width: 2
            }
         }
      });
      view.graphics.removeAll();
      view.graphics.add(graphic);
      view.goTo(graphic.geometry.extent.expand(1.5));
   };

   const zoomToFeatureTow = async (value, fieldName, layerId) => {
      const layer = new FeatureLayer({
         url: `https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/${layerId}`
      });
      const query = new Query();
      query.where = `${fieldName} = '${value}'`;
      query.returnGeometry = true;
      const response = await layer.queryFeatures(query);
      const geometry = response.features[0].geometry;
      const graphic = new Graphic({
         geometry,
         symbol: {
            type: "simple-fill",
            color: null,
            outline: {
               color: [51, 68, 255],
               width: 2
            }
         }
      });
      view.graphics.removeAll();
      view.graphics.add(graphic);
      // view.goTo(graphic.geometry.extand(2.0));
   };

   const zoomToFeatureThree = async (value, fieldName, layerId, ulbCode) => {
      try {
         const layer = new FeatureLayer({
            url: `https://urbangis.mp.gov.in/server/rest/services/temp_n/urbangis_temp/FeatureServer/${layerId}`
         });

         const query = new Query();
         query.where = `ulb_cd = '${ulbCode}' AND ${fieldName} = '${value}'`;
         query.returnGeometry = true;

         const response = await layer.queryFeatures(query);

         if (response.features.length === 0) {
            console.warn("No features found for the given query");
            return;
         }

         const geometry = response.features[0].geometry;

         const graphic = new Graphic({
            geometry,
            symbol: {
               type: "simple-fill",
               color: [0, 0, 0, 0],
               outline: {
                  color: [0, 0, 0, 0],
               }
            }
         });

         view.graphics.removeAll();
         view.graphics.add(graphic);
         view.goTo(graphic.geometry.extent.expand(1.5));

      } catch (error) {
         console.error("Error in zoomToFeatureThree:", error);
      }
   };

   const handleRefresh = () => {
      setUlbs([])
      setSelectedDistrict("")
      setDistrictCode("")
      setUlbCode('')
      setSelectedUlb('')
      setWardCode('')
      setWards([])
      setSearchInput("")
      setSelectedSearch("")
      setWardCode("")
      setPropertyInfo([])
      setSelectedWard("")
      setSelectedSearch('')
      setLeftLayerData([])
      setRightLayerData([])
      view.graphics.removeAll();
      view.goTo({
         center: [77.4126, 23.2599],
         zoom: 6
      });
   }

   const customStyles = {
      control: (provided, state) => ({
         ...provided,
         boxShadow: state.isFocused && !state.hasValue ? "0 0 0 1px #ccc" : "none",
         borderColor: state.isFocused && !state.hasValue ? "#ccc" : "#ccc",
         "&:hover": {
            borderColor: "#ccc",
         },
         fontSize: "12px",
      }),
      option: (provided, state) => ({
         ...provided,
         backgroundColor: state.isSelected
            ? "#d1ede9"
            : state.isFocused
               ? "#d1ede9"
               : "#FFFFFF",
         color: "#000",
         fontSize: "12px",
         padding: "10px",
      }),
      singleValue: (provided) => ({
         ...provided,
         color: "#000",
         fontSize: "12px",
      }),
      placeholder: (base) => ({
         ...base,
         color: "#000",
         fontSize: "12px",
      }),
      input: (provided) => ({
         ...provided,
         color: "#000",
         fontSize: "12px",
      }),
   };

   const yearOptions = [
      { value: '6', label: '2022' },
      { value: '8', label: '2024' },
   ];



   return (
      <>
         <div className="tooltitlediv linear-gradian-bg">
            <h6 className='tools-title m-0 text-white'>Change Analysis</h6>
         </div>

         <div className="row">
            <div className="col-md-6">
               <div className="changepaneldiv">
                  <label htmlFor="yearSelect" className="lebel-card-text mt-2 mb-2">Left Panel </label>
                  <Select
                     options={yearOptions}
                     value={
                        selectedYearLeft
                           ? yearOptions.find((opt) => opt.value === selectedYearLeft)
                           : null
                     }
                     onChange={(selectedOption) => {
                        setSelectedYearLeft(selectedOption?.value || '');
                     }}
                     placeholder="Select year"
                     isClearable={true}
                     styles={customStyles}
                  />
               </div>
            </div>
            <div className="col-md-6">
               <div className="changepaneldiv">
                  <label htmlFor="yearSelect" className="lebel-card-text mt-2 mb-2">Right Panel</label>
                  <Select
                     options={yearOptions}
                     value={
                        selectedYearRight
                           ? yearOptions.find((opt) => opt.value === selectedYearRight)
                           : null
                     }
                     onChange={(selectedOption) => {
                        setSelectedYearRight(selectedOption?.value || '');
                     }}
                     placeholder="Select year"
                     isClearable={true}
                     styles={customStyles}
                  />
               </div>
            </div>
         </div>
         <div className="mt-2">
            <label className="lebel-card-text">Select District</label>
            <Select
               options={districts.map((district) => ({
                  value: district.districtCode,
                  label: district.districtName,
               })).sort((a, b) => a.label.localeCompare(b.label))}
               value={districtCode ? { value: districtCode, label: selectedDistrict } : null}
               onChange={(selectedOption) => {
                  if (!selectedOption) {
                     handleRefresh();
                  } else {
                     handleDistrictChange({ target: { value: selectedOption?.value || "" } });
                  }
               }}
               placeholder="Select District"
               isClearable={true}
               styles={customStyles}
               // className="mt-2"
               isDisabled={authData?.UserType === "districtuser" || authData?.UserType === "User"}
            />
         </div>
         <div className="mt-2">
            <label className="lebel-card-text">Select ULB</label>
            <Select
               options={ulbs.map((ulb) => ({
                  value: ulb.ulbCode,
                  label: ulb.ulbName,
               })).sort((a, b) => a.label.localeCompare(b.label))}
               value={ulbCode ? { value: ulbCode, label: selectedUlb } : null}
               onChange={(selectedOption) =>
                  handleUlbChange({ target: { value: selectedOption?.value || "" } })
               }
               placeholder={"Select ULB"}
               isClearable={true}
               styles={customStyles}
               // className="mt-2"
               isDisabled={!districtCode}
            // isDisabled={authData?.UserType === "districtuser" || authData?.UserType === "User"}
            />
         </div>
         {Array.isArray(leftLayerData) && Array.isArray(rightLayerData) && leftLayerData.length > 0 && rightLayerData.length > 0 && (
            <div className='parameterdiv change-table'
               style={{
                  backgroundColor: isTheme === 'light' ? '#E5EFFF' : "#1E4580"
               }}
            >
               <h6 className="sidebarbuttontitle mt-2 mb-2 text-black"
                  style={{ fontSize: "12px" }}
               >Area (sqkm) Change Statistics (2022 vs 2024)</h6>
               <div className="">
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                     <thead>
                        <tr>
                           <th className="resultleftdiv"
                              style={{ fontSize: "12px" }}
                           >Class Name</th>
                           <th className="resultleftdiv text-start"
                              style={{ fontSize: "12px" }}
                           >2022</th>
                           <th className="resultleftdiv text-start"
                              style={{ fontSize: "12px" }}
                           >2024</th>
                           <th className="resultleftdiv text-end"
                              style={{ fontSize: "12px" }}
                           >Difference</th>
                        </tr>
                     </thead>
                     <tbody>
                        {leftLayerData.map((leftItem, index) => {
                           const rightItem = rightLayerData.find(item => item.attributes.classname === leftItem.attributes.classname);
                           const leftArea = leftItem.attributes.sum_area || 0;
                           const rightArea = rightItem ? rightItem.attributes.sum_area : 0;
                           const difference = rightArea - leftArea;

                           return (
                              <tr key={index}>
                                 <td className="resultleftdiv text-start">{leftItem.attributes.classname}</td>
                                 <td className="resultleftdiv text-start">{leftArea.toFixed(2)}</td>
                                 <td className="resultleftdiv text-start">{rightArea.toFixed(2)}</td>
                                 <td className="resultleftdiv text-end" style={{ color: difference === 0 ? '#000' : difference > 0 ? 'green' : 'red' }}>
                                    {difference.toFixed(2)}
                                 </td>
                              </tr>
                           );
                        })}
                     </tbody>
                  </table>
               </div>
            </div>
         )}
      </>
   )
}

export default ChangeTool